import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { useApi } from "../../lib/useApi";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Loader, ErrorState, PageHeader } from "../../components/common/States";
import { Field } from "../public/Register";
import SessionsCard from "../../components/security/SessionsCard";
import NotificationSettings from "../../components/notifications/NotificationSettings";

const ProfileForm = ({ profile }) => {
  const { refresh } = useAuth();
  const [f, setF] = useState({ name: profile.name, phone: profile.phone, city: profile.city, country: profile.country });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (f.name.trim().length < 2) { setErr("Nom requis (2 caractères min.)"); return; }
    setErr(""); setSaving(true);
    try { await api.put("/me/profile", f); await refresh(); toast.success("Profil enregistré"); }
    catch (e2) { toast.error(formatApiError(e2)); } finally { setSaving(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form onSubmit={save} className="card-surface space-y-4 p-6" noValidate>
      <p className="font-display font-semibold">Informations personnelles</p>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field id="profile-name" label="Nom complet" value={f.name} onChange={set("name")} error={err} />
        <Field id="profile-email" label="Email (identifiant)" value={profile.email} disabled />
        <Field id="profile-phone" label="Téléphone" type="tel" value={f.phone} onChange={set("phone")} />
        <Field id="profile-city" label="Ville" value={f.city} onChange={set("city")} />
        <Field id="profile-country" label="Pays" value={f.country} onChange={set("country")} />
      </div>
      <Button type="submit" disabled={saving} className="rounded-full" data-testid="profile-save-button">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Enregistrer</Button>
    </form>
  );
};

export const PasswordForm = () => {
  const [f, setF] = useState({ current: "", next: "", confirm: "" });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!f.current) errs.current = "Requis";
    if (f.next.length < 8) errs.next = "8 caractères minimum";
    if (f.next !== f.confirm) errs.confirm = "Les mots de passe ne correspondent pas";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true);
    try { await api.put("/me/password", { current_password: f.current, new_password: f.next }); setF({ current: "", next: "", confirm: "" }); toast.success("Mot de passe modifié"); }
    catch (e2) { toast.error(formatApiError(e2)); } finally { setSaving(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form onSubmit={save} className="card-surface space-y-4 p-6" noValidate>
      <p className="font-display font-semibold">Mot de passe</p>
      <div className="grid gap-4 sm:grid-cols-3">
        <Field id="password-current" label="Actuel" type="password" value={f.current} onChange={set("current")} error={errors.current} />
        <Field id="password-new" label="Nouveau" type="password" value={f.next} onChange={set("next")} error={errors.next} />
        <Field id="password-confirm" label="Confirmer" type="password" value={f.confirm} onChange={set("confirm")} error={errors.confirm} />
      </div>
      <Button type="submit" variant="outline" disabled={saving} className="rounded-full" data-testid="password-save-button">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Modifier le mot de passe</Button>
    </form>
  );
};

export default function UserProfile() {
  const { data, loading, error, reload } = useApi("/me/profile");
  if (loading) return <Loader />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  return (
    <div className="max-w-3xl space-y-6">
      <PageHeader title="Mon profil" />
      <ProfileForm profile={data} />
      <PasswordForm />
      <NotificationSettings />
      <SessionsCard />
    </div>
  );
}
